﻿import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded"
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createProduct,
  deleteProduct,
  getNextProductCode,
  getProductById,
  getProducts,
  updateProduct,
} from "../../services/productService"

import {
  getCategories,
} from "../../services/categoryService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const todayDate = () =>
  new Date()
    .toISOString()
    .slice(0, 10)


const PRODUCT_TYPES = [
  {
    value: "FINISHED_GOOD",
    label: "Produit fini",
  },
  {
    value: "SEMI_FINISHED",
    label: "Produit semi-fini",
  },
  {
    value: "RAW_MATERIAL",
    label: "Matière première",
  },
  {
    value: "COMPONENT",
    label: "Composant",
  },
  {
    value: "PACKAGING",
    label: "Emballage",
  },
  {
    value: "TRADE_GOOD",
    label: "Marchandise / revente",
  },
  {
    value: "CONSUMABLE",
    label: "Consommable",
  },
  {
    value: "SERVICE",
    label: "Service",
  },
]


const PRODUCT_UNITS = [
  ["piece", "Pièce"],
  ["kg", "Kilogramme (kg)"],
  ["g", "Gramme (g)"],
  ["tonne", "Tonne"],
  ["litre", "Litre (L)"],
  ["ml", "Millilitre (mL)"],
  ["metre", "Mètre (m)"],
  ["cm", "Centimètre (cm)"],
  ["m2", "Mètre carré (m²)"],
  ["m3", "Mètre cube (m³)"],
  ["carton", "Carton"],
  ["sac", "Sac"],
  ["rouleau", "Rouleau"],
  ["paquet", "Paquet"],
  ["boite", "Boîte"],
  ["palette", "Palette"],
  ["bidon", "Bidon"],
  ["fut", "Fût"],
].map(
  ([value, label]) => ({
    value,
    label,
  })
)


const TYPE_DEFAULTS = {
  FINISHED_GOOD: {
    can_buy: false,
    can_sell: true,
    can_produce: true,
    can_consume: false,
    track_stock: true,
  },

  SEMI_FINISHED: {
    can_buy: false,
    can_sell: false,
    can_produce: true,
    can_consume: true,
    track_stock: true,
  },

  RAW_MATERIAL: {
    can_buy: true,
    can_sell: false,
    can_produce: false,
    can_consume: true,
    track_stock: true,
  },

  COMPONENT: {
    can_buy: true,
    can_sell: false,
    can_produce: false,
    can_consume: true,
    track_stock: true,
  },

  PACKAGING: {
    can_buy: true,
    can_sell: false,
    can_produce: false,
    can_consume: true,
    track_stock: true,
  },

  TRADE_GOOD: {
    can_buy: true,
    can_sell: true,
    can_produce: false,
    can_consume: false,
    track_stock: true,
  },

  CONSUMABLE: {
    can_buy: true,
    can_sell: false,
    can_produce: false,
    can_consume: true,
    track_stock: true,
  },

  SERVICE: {
    can_buy: true,
    can_sell: true,
    can_produce: false,
    can_consume: false,
    track_stock: false,
  },
}


const defaultFormData = {
  code: "",
  name: "",
  category_id: "",

  product_type:
    "FINISHED_GOOD",

  brand:
    "Entreprise principale",

  unit:
    "piece",

  description: "",

  is_active:
    true,

  can_buy:
    false,

  can_sell:
    true,

  can_produce:
    true,

  can_consume:
    false,

  track_stock:
    true,

  price_currency:
    "USD",

  exchange_rate:
    "",

  exchange_rate_date:
    todayDate(),

  factory_price:
    "",

  factory_price_cdf:
    "",

  maison_djo_margin_percent:
    "25",

  depot_relais_margin_percent:
    "25",

  terrain_margin_percent:
    "20",
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatAmount(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatUsd(value) {
  return `${formatAmount(value)} USD`
}


function formatCdf(value) {
  return `${toNumber(value).toLocaleString(
    "fr-FR",
    {
      maximumFractionDigits: 0,
    }
  )} CDF`
}


function calculatePrice(
  base,
  margin
) {
  const amount =
    toNumber(base)

  if (amount <= 0) {
    return 0
  }

  return (
    amount +
    (
      amount *
      toNumber(margin)
    ) /
      100
  )
}


function getProductTypeLabel(
  value
) {
  return (
    PRODUCT_TYPES.find(
      (item) =>
        item.value === value
    )?.label ||
    value ||
    "-"
  )
}


function getUnitLabel(
  value
) {
  return (
    PRODUCT_UNITS.find(
      (item) =>
        item.value === value
    )?.label ||
    value ||
    "-"
  )
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  return fallback
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.08)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.4,
          sm: 1.6,
        },

        py: {
          xs: 1.2,
          sm: 1.3,
        },

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",

        transition:
          "transform 0.18s ease, box-shadow 0.18s ease",

        "&:hover": {
          transform:
            "translateY(-1px)",

          boxShadow:
            "0 9px 24px rgba(15, 23, 42, 0.065)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.2,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            borderRadius: 2.5,

            display: "grid",

            placeItems: "center",

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.75,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 20,
                  sm: 22,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",

                textOverflow: "ellipsis",

                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              whiteSpace: "nowrap",

              overflow: "hidden",

              textOverflow: "ellipsis",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

function ProductsPage() {
  const theme = useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    products,
    setProducts,
  ] = useState([])

  const [
    categories,
    setCategories,
  ] = useState([])

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)


  // =======================================================
  // DIALOG
  // =======================================================

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedProduct,
    setSelectedProduct,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    categoryFilter,
    setCategoryFilter,
  ] = useState("")

  const [
    typeFilter,
    setTypeFilter,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  const closeSnackbar = () => {
    setSnackbar(
      (previous) => ({
        ...previous,
        open: false,
      })
    )
  }


  // =======================================================
  // COMPUTED PRICES
  // =======================================================

  const computedPrices =
    useMemo(() => {
      const rate =
        toNumber(
          formData.exchange_rate
        )

      let baseUsd = 0
      let baseCdf = 0

      if (
        formData.price_currency ===
        "CDF"
      ) {
        baseCdf =
          toNumber(
            formData.factory_price_cdf
          )

        baseUsd =
          rate > 0
            ? baseCdf / rate
            : 0
      } else {
        baseUsd =
          toNumber(
            formData.factory_price
          )

        baseCdf =
          rate > 0
            ? baseUsd * rate
            : 0
      }

      return {
        baseUsd,
        baseCdf,

        distributorUsd:
          calculatePrice(
            baseUsd,
            formData.maison_djo_margin_percent
          ),

        relayUsd:
          calculatePrice(
            baseUsd,
            formData.depot_relais_margin_percent
          ),

        terrainUsd:
          calculatePrice(
            baseUsd,
            formData.terrain_margin_percent
          ),

        rate,
      }
    }, [
      formData,
    ])


  // =======================================================
  // LOAD DATA
  // =======================================================

  const loadData =
    async () => {
      try {
        setLoading(true)

        const [
          productData,
          categoryData,
        ] =
          await Promise.all([
            getProducts(),
            getCategories(),
          ])

        setProducts(
          Array.isArray(productData)
            ? productData
            : []
        )

        setCategories(
          Array.isArray(categoryData)
            ? categoryData
            : []
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger le catalogue."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  useEffect(() => {
    loadData()
  }, [])


  // =======================================================
  // RESET PAGINATION AFTER FILTER
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,
        page: 0,
      })
    )
  }, [
    search,
    categoryFilter,
    typeFilter,
    statusFilter,
  ])


  // =======================================================
  // RESET FORM
  // =======================================================

  const resetForm = () => {
    setSelectedProduct(null)

    setFormData({
      ...defaultFormData,

      exchange_rate_date:
        todayDate(),
    })
  }


  // =======================================================
  // OPEN ADD
  // =======================================================

  const openAddDialog =
    async () => {
      try {
        resetForm()

        const result =
          await getNextProductCode()

        setFormData({
          ...defaultFormData,

          code:
            result?.code || "",

          exchange_rate_date:
            todayDate(),
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          "Impossible de générer le code produit.",
          "error"
        )
      }
    }


  // =======================================================
  // OPEN EDIT
  // =======================================================

  const openEditDialog =
    async (id) => {
      try {
        const product =
          await getProductById(id)

        setSelectedProduct(
          product
        )

        const defaults =
          TYPE_DEFAULTS[
            product.product_type
          ] ||
          TYPE_DEFAULTS.FINISHED_GOOD

        setFormData({
          ...defaultFormData,
          ...product,

          category_id:
            product.category_id ||
            "",

          exchange_rate:
            product.exchange_rate ??
            "",

          factory_price:
            product.factory_price ??
            "",

          factory_price_cdf:
            product.factory_price_cdf ??
            "",

          exchange_rate_date:
            product.exchange_rate_date ||
            todayDate(),

          can_buy:
            product.can_buy ??
            defaults.can_buy,

          can_sell:
            product.can_sell ??
            defaults.can_sell,

          can_produce:
            product.can_produce ??
            defaults.can_produce,

          can_consume:
            product.can_consume ??
            defaults.can_consume,

          track_stock:
            product.track_stock ??
            defaults.track_stock,
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger le produit."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // CLOSE DIALOG
  // =======================================================

  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  // =======================================================
  // FORM CHANGE
  // =======================================================

  const handleChange =
    (event) => {
      const {
        name,
        value,
        checked,
        type,
      } = event.target

      setFormData(
        (previous) => {
          if (
            name ===
            "product_type"
          ) {
            const defaults =
              TYPE_DEFAULTS[value] ||
              {}

            return {
              ...previous,

              product_type:
                value,

              ...defaults,
            }
          }

          if (
            type ===
            "checkbox"
          ) {
            return {
              ...previous,

              [name]:
                checked,
            }
          }

          return {
            ...previous,

            [name]:
              value,
          }
        }
      )
    }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !formData.name.trim() ||
        !formData.category_id ||
        !formData.product_type ||
        !formData.unit
      ) {
        showSnackbar(
          "Nom, catégorie, nature et unité sont obligatoires.",
          "warning"
        )

        return
      }

      if (
        formData.price_currency ===
          "CDF" &&
        toNumber(
          formData.factory_price_cdf
        ) > 0 &&
        toNumber(
          formData.exchange_rate
        ) <= 0
      ) {
        showSnackbar(
          "Le taux CDF/USD est requis pour un montant saisi en CDF.",
          "warning"
        )

        return
      }

      const payload = {
        code:
          formData.code || null,

        name:
          formData.name.trim(),

        category_id:
          Number(
            formData.category_id
          ),

        product_type:
          formData.product_type,

        brand:
          formData.brand?.trim() ||
          null,

        unit:
          formData.unit,

        description:
          formData.description?.trim() ||
          null,

        is_active:
          Boolean(
            formData.is_active
          ),

        can_buy:
          Boolean(
            formData.can_buy
          ),

        can_sell:
          Boolean(
            formData.can_sell
          ),

        can_produce:
          Boolean(
            formData.can_produce
          ),

        can_consume:
          Boolean(
            formData.can_consume
          ),

        track_stock:
          Boolean(
            formData.track_stock
          ),

        price_currency:
          formData.price_currency,

        exchange_rate:
          formData.exchange_rate ===
          ""
            ? null
            : Number(
                formData.exchange_rate
              ),

        exchange_rate_date:
          formData.exchange_rate_date ||
          null,

        factory_price:
          Number(
            computedPrices.baseUsd ||
            0
          ),

        factory_price_cdf:
          computedPrices.baseCdf > 0
            ? Number(
                computedPrices.baseCdf
              )
            : null,

        maison_djo_margin_percent:
          Number(
            formData.maison_djo_margin_percent ||
            0
          ),

        depot_relais_margin_percent:
          Number(
            formData.depot_relais_margin_percent ||
            0
          ),

        terrain_margin_percent:
          Number(
            formData.terrain_margin_percent ||
            0
          ),
      }

      try {
        setSaving(true)

        if (
          selectedProduct?.id
        ) {
          await updateProduct(
            selectedProduct.id,
            payload
          )
        } else {
          await createProduct(
            payload
          )
        }

        showSnackbar(
          selectedProduct
            ? "Produit modifié avec succès."
            : "Produit créé avec succès."
        )

        await loadData()

        setOpen(false)
        resetForm()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible d'enregistrer le produit."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (row) => {
      const confirmed =
        window.confirm(
          `Supprimer « ${row.name} » ?`
        )

      if (!confirmed) {
        return
      }

      try {
        await deleteProduct(
          row.id
        )

        showSnackbar(
          "Produit supprimé."
        )

        await loadData()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de supprimer le produit."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // KPI
  // =======================================================

  const stats =
    useMemo(() => {
      return products.reduce(
        (
          accumulator,
          product
        ) => {
          accumulator.total +=
            1

          if (
            product.is_active
          ) {
            accumulator.active +=
              1
          }

          if (
            product.can_sell
          ) {
            accumulator.sellable +=
              1
          }

          if (
            product.track_stock
          ) {
            accumulator.stockTracked +=
              1
          }

          return accumulator
        },
        {
          total: 0,
          active: 0,
          sellable: 0,
          stockTracked: 0,
        }
      )
    }, [
      products,
    ])


  // =======================================================
  // FILTER
  // =======================================================

  const filteredProducts =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return products.filter(
        (item) => {
          const searchMatch =
            !keyword ||
            [
              item.code,
              item.name,
              item.category,
              item.brand,
              getProductTypeLabel(
                item.product_type
              ),
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          const categoryMatch =
            !categoryFilter ||
            String(
              item.category_id
            ) ===
              String(
                categoryFilter
              )

          const typeMatch =
            !typeFilter ||
            item.product_type ===
              typeFilter

          const statusMatch =
            statusFilter === "" ||
            String(
              Boolean(
                item.is_active
              )
            ) ===
              statusFilter

          return (
            searchMatch &&
            categoryMatch &&
            typeMatch &&
            statusMatch
          )
        }
      )
    }, [
      products,
      search,
      categoryFilter,
      typeFilter,
      statusFilter,
    ])


  // =======================================================
  // MOBILE PAGINATION
  // =======================================================

  const paginatedProducts =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredProducts.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredProducts,
      paginationModel.page,
    ])


  // =======================================================
  // KEEP VALID PAGE
  // =======================================================

  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredProducts.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,
        })
      )
    }
  }, [
    filteredProducts.length,
    paginationModel.page,
  ])


  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters = () => {
    setSearch("")
    setCategoryFilter("")
    setTypeFilter("")
    setStatusFilter("")

    setPaginationModel({
      page: 0,
      pageSize:
        ROWS_PER_PAGE,
    })
  }


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "name",

          headerName:
            "Produit",

          minWidth: 180,

          flex: 1.15,

          renderCell:
            (params) => (
              <Stack
                spacing={0.08}
                sx={{
                  minWidth: 0,

                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={900}
                  color="#0f172a"
                  noWrap
                  title={
                    params.row.name ||
                    ""
                  }
                >
                  {params.row.name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={
                    `${
                      params.row.code ||
                      "-"
                    }${
                      params.row.brand
                        ? ` • ${params.row.brand}`
                        : ""
                    }`
                  }
                >
                  {params.row.code ||
                    "-"}

                  {params.row.brand
                    ? ` • ${params.row.brand}`
                    : ""}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "classification",

          headerName:
            "Classification",

          minWidth: 185,

          flex: 1.1,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.12}
                sx={{
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  noWrap
                  title={
                    params.row.category ||
                    ""
                  }
                >
                  {params.row.category ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                >
                  {getProductTypeLabel(
                    params.row.product_type
                  )}
                </Typography>
              </Stack>
            ),
        },


        {
          field: "stock",

          headerName:
            "Stock / unité",

          width: 125,

          renderCell:
            (params) => (
              <Stack
                spacing={0.08}
              >
                <Typography
                  variant="body2"
                  fontWeight={800}
                >
                  {params.row.track_stock
                    ? toNumber(
                        params.value
                      )
                    : "N/A"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                >
                  {getUnitLabel(
                    params.row.unit
                  )}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "factory_price",

          headerName:
            "Prix / coût base",

          minWidth: 135,

          flex: 0.85,

          renderCell:
            (params) => (
              <Stack
                spacing={0.08}
              >
                <Typography
                  variant="body2"
                  fontWeight={900}
                  noWrap
                >
                  {formatUsd(
                    params.value
                  )}
                </Typography>

                {toNumber(
                  params.row
                    .factory_price_cdf
                ) > 0 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                  >
                    {formatCdf(
                      params.row
                        .factory_price_cdf
                    )}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                  >
                    Référence USD
                  </Typography>
                )}
              </Stack>
            ),
        },


        {
          field:
            "capabilities",

          headerName:
            "Usage",

          minWidth: 205,

          flex: 1.25,

          sortable: false,

          renderCell:
            (params) => (
              <Box
                sx={{
                  display: "flex",

                  flexWrap: "wrap",

                  gap: 0.4,

                  py: 0.5,
                }}
              >
                {params.row.can_buy && (
                  <Chip
                    size="small"
                    label="Achat"
                    variant="outlined"
                  />
                )}

                {params.row.can_sell && (
                  <Chip
                    size="small"
                    color="success"
                    label="Vente"
                  />
                )}

                {params.row.can_produce && (
                  <Chip
                    size="small"
                    color="primary"
                    label="Production"
                  />
                )}

                {params.row.can_consume && (
                  <Chip
                    size="small"
                    color="warning"
                    label="BOM"
                  />
                )}
              </Box>
            ),
        },


        {
          field:
            "is_active",

          headerName:
            "Statut",

          width: 90,

          renderCell:
            (params) => (
              <Chip
                size="small"

                label={
                  params.value
                    ? "Actif"
                    : "Inactif"
                }

                color={
                  params.value
                    ? "success"
                    : "default"
                }

                variant={
                  params.value
                    ? "filled"
                    : "outlined"
                }

                sx={{
                  fontWeight: 800,
                }}
              />
            ),
        },


        {
          field: "actions",

          headerName:
            "Actions",

          width: 182,

          sortable: false,

          filterable: false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => (
              <Stack
                direction="row"
                spacing={0.65}
              >
                <Button
                  size="small"

                  variant="contained"

                  disableElevation

                  startIcon={
                    <EditIcon />
                  }

                  onClick={() =>
                    openEditDialog(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 82,

                    px: 1,
                  }}
                >
                  Modifier
                </Button>


                <Button
                  size="small"

                  variant="outlined"

                  color="error"

                  onClick={() =>
                    handleDelete(
                      params.row
                    )
                  }

                  sx={{
                    minWidth: 86,

                    px: 1,
                  }}
                >
                  Supprimer
                </Button>
              </Stack>
            ),
        },
      ],
      []
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight: 800,

              borderRadius: 2.5,
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                sm:
                  "minmax(0, 1fr) auto",
              },

            alignItems:
              "start",

            columnGap: 2,

            rowGap: 1.25,

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Catalogue produits
            </Typography>


            <Typography
              variant="body2"

              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 850,
              }}
            >
              Produits vendus, matières premières,
              composants, marchandises, consommables,
              emballages et services.
            </Typography>
          </Box>


          <Button
            variant="contained"

            disableElevation

            startIcon={
              <AddIcon />
            }

            onClick={
              openAddDialog
            }

            sx={{
              minHeight: 40,

              px: 2.2,

              minWidth: {
                xs: "100%",
                sm: 170,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            Ajouter article
          </Button>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"

          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,

            alignItems: "center",

            bgcolor:
              "rgba(14, 165, 233, 0.025)",
          }}
        >
          La catégorie classe l'article tandis que sa
          nature décrit son rôle. Les propriétés Achat,
          Vente, Production et Consommation permettent
          d'adapter RLSys à différents secteurs d'activité.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                md:
                  "repeat(4, minmax(0, 1fr))",
              },

            gap: 1.15,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Produits"

            value={
              stats.total
            }

            caption="Articles du catalogue"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <Inventory2RoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Actifs"

            value={
              stats.active
            }

            caption="Disponibles actuellement"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <CheckCircleRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Vendables"

            value={
              stats.sellable
            }

            caption="Autorisés à la vente"

            accent="#7c3aed"

            background="rgba(124, 58, 237, 0.09)"

            icon={
              <PointOfSaleRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Gérés en stock"

            value={
              stats.stockTracked
            }

            caption="Avec suivi de quantité"

            accent="#ea580c"

            background="rgba(234, 88, 12, 0.09)"

            icon={
              <WarehouseRoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 6px 18px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                {
                  xs: "1fr",

                  sm:
                    "repeat(2, minmax(0, 1fr))",

                  lg:
                    "minmax(220px, 1.4fr) minmax(160px, 0.8fr) minmax(175px, 0.85fr) minmax(130px, 0.65fr) auto",
                },

              gap: 1,

              alignItems:
                "center",
            }}
          >
            <TextField
              fullWidth

              size="small"

              label="Recherche"

              placeholder="Code, article, marque..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select

              fullWidth

              size="small"

              label="Catégorie"

              value={
                categoryFilter
              }

              onChange={(
                event
              ) =>
                setCategoryFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Toutes
              </MenuItem>

              {categories.map(
                (item) => (
                  <MenuItem
                    key={
                      item.id
                    }

                    value={
                      item.id
                    }
                  >
                    {item.name}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select

              fullWidth

              size="small"

              label="Nature"

              value={
                typeFilter
              }

              onChange={(
                event
              ) =>
                setTypeFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Toutes
              </MenuItem>

              {PRODUCT_TYPES.map(
                (item) => (
                  <MenuItem
                    key={
                      item.value
                    }

                    value={
                      item.value
                    }
                  >
                    {item.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select

              fullWidth

              size="small"

              label="Statut"

              value={
                statusFilter
              }

              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="true">
                Actif
              </MenuItem>

              <MenuItem value="false">
                Inactif
              </MenuItem>
            </TextField>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  lg: 115,
                },
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            PRODUCT REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            minWidth: 0,

            overflow: "hidden",

            borderRadius: 3.5,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >

          {/* HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des produits
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Articles, classification, stock, prix et usages métier
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredProducts.length} produit${
                filteredProducts.length >
                1
                  ? "s"
                  : ""
              }`}

              sx={{
                fontWeight: 800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflow: "hidden",

                bgcolor: "#ffffff",
              }}
            >
              <DataGrid
                autoHeight

                rows={
                  filteredProducts
                }

                columns={
                  columns
                }

                loading={
                  loading
                }

                disableRowSelectionOnClick

                disableColumnSelector

                pagination

                paginationModel={
                  paginationModel
                }

                onPaginationModelChange={
                  setPaginationModel
                }

                pageSizeOptions={[
                  ROWS_PER_PAGE,
                ]}

                rowHeight={64}

                columnHeaderHeight={48}

                localeText={{
                  noRowsLabel:
                    "Aucun produit enregistré",

                  noResultsOverlayLabel:
                    "Aucun résultat",

                  MuiTablePagination:
                    {
                      labelRowsPerPage:
                        "",

                      labelDisplayedRows:
                        ({
                          from,
                          to,
                          count,
                        }) =>
                          `${from}–${to} sur ${count}`,
                    },
                }}

                sx={{
                  border: 0,

                  width: "100%",

                  minWidth: 0,

                  color: "#334155",

                  "& .MuiDataGrid-columnHeaders":
                    {
                      bgcolor:
                        "#f8fafc",

                      borderBottom:
                        "1px solid rgba(148, 163, 184, 0.22)",
                    },

                  "& .MuiDataGrid-columnHeader":
                    {
                      bgcolor:
                        "#f8fafc",

                      px: 1.05,
                    },

                  "& .MuiDataGrid-columnHeaderTitle":
                    {
                      fontWeight: 900,

                      color:
                        "#334155",

                      fontSize:
                        "0.78rem",
                    },

                  "& .MuiDataGrid-cell":
                    {
                      px: 1.05,

                      minWidth: 0,

                      display: "flex",

                      alignItems:
                        "center",

                      borderColor:
                        "rgba(148, 163, 184, 0.14)",
                    },

                  "& .MuiDataGrid-row:hover":
                    {
                      bgcolor:
                        "#f8fbff",
                    },

                  "& .MuiDataGrid-footerContainer":
                    {
                      minHeight: 50,

                      bgcolor:
                        "#ffffff",

                      borderTop:
                        "1px solid rgba(148, 163, 184, 0.20)",
                    },

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-scrollbar--horizontal":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-virtualScroller":
                    {
                      overflowX:
                        "hidden",
                    },
                }}
              />
            </Box>
          ) : (

            /* =================================================
               TABLET / MOBILE
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.15,
                  sm: 1.35,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des produits...
                  </Typography>
                </Box>
              ) : paginatedProducts.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun produit trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={1.1}
                >
                  {paginatedProducts.map(
                    (product) => (
                      <Paper
                        key={
                          product.id
                        }

                        variant="outlined"

                        sx={{
                          p: {
                            xs: 1.4,
                            sm: 1.6,
                          },

                          borderRadius: 3,

                          bgcolor:
                            "#ffffff",

                          borderColor:
                            "rgba(148, 163, 184, 0.22)",
                        }}
                      >
                        <Stack
                          spacing={1.35}
                        >

                          {/* TOP */}

                          <Box
                            sx={{
                              display:
                                "flex",

                              justifyContent:
                                "space-between",

                              alignItems:
                                "flex-start",

                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                fontWeight={900}
                                color="#0f172a"
                                sx={{
                                  overflowWrap:
                                    "anywhere",
                                }}
                              >
                                {product.name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {product.code ||
                                  "-"}

                                {product.brand
                                  ? ` • ${product.brand}`
                                  : ""}
                              </Typography>
                            </Box>


                            <Chip
                              size="small"

                              label={
                                product.is_active
                                  ? "Actif"
                                  : "Inactif"
                              }

                              color={
                                product.is_active
                                  ? "success"
                                  : "default"
                              }

                              variant={
                                product.is_active
                                  ? "filled"
                                  : "outlined"
                              }

                              sx={{
                                fontWeight:
                                  800,
                              }}
                            />
                          </Box>


                          {/* CLASSIFICATION */}

                          <Box
                            sx={{
                              display: "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 1,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Catégorie
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={800}
                              >
                                {product.category ||
                                  "-"}
                              </Typography>
                            </Box>


                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Nature
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={800}
                              >
                                {getProductTypeLabel(
                                  product.product_type
                                )}
                              </Typography>
                            </Box>
                          </Box>


                          {/* STOCK / PRICE */}

                          <Box
                            sx={{
                              display: "grid",

                              gridTemplateColumns:
                                {
                                  xs:
                                    "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 1,
                            }}
                          >
                            <Paper
                              variant="outlined"

                              sx={{
                                p: 1,

                                borderRadius:
                                  2.5,

                                bgcolor:
                                  "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Stock
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {product.track_stock
                                  ? `${toNumber(
                                      product.stock
                                    )} ${getUnitLabel(
                                      product.unit
                                    )}`
                                  : "Non suivi"}
                              </Typography>
                            </Paper>


                            <Paper
                              variant="outlined"

                              sx={{
                                p: 1,

                                borderRadius:
                                  2.5,

                                bgcolor:
                                  "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Prix / coût base
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {formatUsd(
                                  product.factory_price
                                )}
                              </Typography>
                            </Paper>
                          </Box>


                          {/* USAGE */}

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              Utilisation métier
                            </Typography>

                            <Box
                              sx={{
                                display:
                                  "flex",

                                flexWrap:
                                  "wrap",

                                gap: 0.5,

                                mt: 0.5,
                              }}
                            >
                              {product.can_buy && (
                                <Chip
                                  size="small"
                                  label="Achat"
                                  variant="outlined"
                                />
                              )}

                              {product.can_sell && (
                                <Chip
                                  size="small"
                                  label="Vente"
                                  color="success"
                                />
                              )}

                              {product.can_produce && (
                                <Chip
                                  size="small"
                                  label="Production"
                                  color="primary"
                                />
                              )}

                              {product.can_consume && (
                                <Chip
                                  size="small"
                                  label="BOM"
                                  color="warning"
                                />
                              )}

                              {!product.can_buy &&
                              !product.can_sell &&
                              !product.can_produce &&
                              !product.can_consume ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Aucun usage défini
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>


                          {/* ACTIONS */}

                          <Box
                            sx={{
                              display:
                                "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 0.8,
                            }}
                          >
                            <Button
                              size="small"

                              variant="contained"

                              disableElevation

                              startIcon={
                                <EditIcon />
                              }

                              onClick={() =>
                                openEditDialog(
                                  product.id
                                )
                              }
                            >
                              Modifier
                            </Button>


                            <Button
                              size="small"

                              variant="outlined"

                              color="error"

                              onClick={() =>
                                handleDelete(
                                  product
                                )
                              }
                            >
                              Supprimer
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Stack>
              )}


              {/* MOBILE PAGINATION */}

              <TablePagination
                component="div"

                count={
                  filteredProducts.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius:
                    2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight:
                        50,

                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display:
                        "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display:
                        "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          PRODUCT DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          handleClose
        }

        fullWidth

        maxWidth="lg"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "92dvh",
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.4,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedProduct
              ? "Modifier l'article"
              : "Nouvel article"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            {selectedProduct
              ? "Modifiez les informations, l'utilisation métier et les paramètres tarifaires de cet article."
              : "Créez un article et définissez sa classification, son usage métier et son prix de référence."}
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >

            {/* =================================================
                IDENTIFICATION
            ================================================= */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.7,
                },

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.25)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"

                sx={{
                  mb: 1.4,
                }}
              >
                Identification de l'article
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "minmax(170px, 0.65fr) minmax(260px, 1.35fr)",
                    },

                  gap: 1.5,

                  mb: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Code"

                  name="code"

                  value={
                    formData.code
                  }

                  disabled={
                    !selectedProduct ||
                    saving
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  helperText={
                    selectedProduct
                      ? "Code de l'article"
                      : "Généré automatiquement"
                  }
                />


                <TextField
                  size="small"

                  label="Nom"

                  name="name"

                  value={
                    formData.name
                  }

                  onChange={
                    handleChange
                  }

                  required

                  fullWidth

                  disabled={
                    saving
                  }
                />
              </Box>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(3, minmax(0, 1fr))",
                    },

                  gap: 1.5,

                  mb: 1.5,
                }}
              >
                <TextField
                  select

                  size="small"

                  label="Catégorie"

                  name="category_id"

                  value={
                    formData.category_id
                  }

                  onChange={
                    handleChange
                  }

                  required

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {categories.map(
                    (item) => (
                      <MenuItem
                        key={
                          item.id
                        }

                        value={
                          item.id
                        }
                      >
                        {item.name}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select

                  size="small"

                  label="Nature"

                  name="product_type"

                  value={
                    formData.product_type
                  }

                  onChange={
                    handleChange
                  }

                  required

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {PRODUCT_TYPES.map(
                    (item) => (
                      <MenuItem
                        key={
                          item.value
                        }

                        value={
                          item.value
                        }
                      >
                        {item.label}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select

                  size="small"

                  label="Unité"

                  name="unit"

                  value={
                    formData.unit
                  }

                  onChange={
                    handleChange
                  }

                  required

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {PRODUCT_UNITS.map(
                    (item) => (
                      <MenuItem
                        key={
                          item.value
                        }

                        value={
                          item.value
                        }
                      >
                        {item.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>


              <TextField
                size="small"

                label="Marque / référence"

                name="brand"

                value={
                  formData.brand ||
                  ""
                }

                onChange={
                  handleChange
                }

                fullWidth

                disabled={
                  saving
                }
              />
            </Paper>


            {/* =================================================
                BUSINESS USAGE
            ================================================= */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.7,
                },

                borderRadius: 3,

                bgcolor:
                  "#f8fafc",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Utilisation métier
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"

                sx={{
                  display: "block",

                  mt: 0.25,

                  mb: 1,
                }}
              >
                Ces propriétés sont proposées automatiquement
                selon la nature du produit, mais peuvent être ajustées.
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      sm:
                        "repeat(2, minmax(0, 1fr))",

                      md:
                        "repeat(5, minmax(0, 1fr))",
                    },

                  gap: 0.5,
                }}
              >
                {[
                  [
                    "can_buy",
                    "Peut être acheté",
                  ],
                  [
                    "can_sell",
                    "Peut être vendu",
                  ],
                  [
                    "can_produce",
                    "Peut être produit",
                  ],
                  [
                    "can_consume",
                    "Entre dans une BOM",
                  ],
                  [
                    "track_stock",
                    "Géré en stock",
                  ],
                ].map(
                  ([
                    name,
                    label,
                  ]) => (
                    <FormControlLabel
                      key={
                        name
                      }

                      sx={{
                        m: 0,

                        minWidth: 0,
                      }}

                      control={
                        <Checkbox
                          name={
                            name
                          }

                          checked={
                            Boolean(
                              formData[
                                name
                              ]
                            )
                          }

                          onChange={
                            handleChange
                          }

                          disabled={
                            saving
                          }
                        />
                      }

                      label={
                        <Typography
                          variant="body2"
                          fontWeight={700}
                        >
                          {label}
                        </Typography>
                      }
                    />
                  )
                )}
              </Box>
            </Paper>


            {/* =================================================
                BUSINESS PRICE INFO
            ================================================= */}

            {formData.can_sell ? (
              <Alert
                severity="info"

                variant="outlined"

                sx={{
                  borderRadius: 3,
                }}
              >
                Cet article peut être vendu. Les prix commerciaux
                sont calculés à partir du prix de base et des marges.
              </Alert>
            ) : (
              <Alert
                severity="warning"

                variant="outlined"

                sx={{
                  borderRadius: 3,
                }}
              >
                Cet article n'est pas destiné directement à la vente.
                Le montant ci-dessous représente un coût ou prix de
                référence facultatif.
              </Alert>
            )}


            {/* =================================================
                BASE PRICE
            ================================================= */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.7,
                },

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.25)",
              }}
            >
              <Typography
                fontWeight={900}

                color="#0f172a"

                sx={{
                  mb: 1.4,
                }}
              >
                Prix / coût de référence
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(3, minmax(0, 1fr))",
                    },

                  gap: 1.5,

                  mb: 1.5,
                }}
              >
                <TextField
                  select

                  size="small"

                  label="Devise"

                  name="price_currency"

                  value={
                    formData.price_currency
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="USD">
                    USD
                  </MenuItem>

                  <MenuItem value="CDF">
                    CDF
                  </MenuItem>
                </TextField>


                <TextField
                  size="small"

                  label="Taux CDF / USD"

                  name="exchange_rate"

                  type="number"

                  value={
                    formData.exchange_rate
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />


                <TextField
                  size="small"

                  type="date"

                  label="Date du taux"

                  name="exchange_rate_date"

                  value={
                    formData.exchange_rate_date
                  }

                  onChange={
                    handleChange
                  }

                  InputLabelProps={{
                    shrink: true,
                  }}

                  fullWidth

                  disabled={
                    saving
                  }
                />
              </Box>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                {formData.price_currency ===
                "CDF" ? (
                  <TextField
                    size="small"

                    label={
                      formData.can_sell
                        ? "Prix de base CDF"
                        : "Coût de référence CDF"
                    }

                    name="factory_price_cdf"

                    type="number"

                    value={
                      formData.factory_price_cdf
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    disabled={
                      saving
                    }
                  />
                ) : (
                  <TextField
                    size="small"

                    label={
                      formData.can_sell
                        ? "Prix de base USD"
                        : "Coût de référence USD"
                    }

                    name="factory_price"

                    type="number"

                    value={
                      formData.factory_price
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    disabled={
                      saving
                    }
                  />
                )}


                <Paper
                  variant="outlined"

                  sx={{
                    px: 1.5,

                    py: 0.9,

                    borderRadius: 2.5,

                    bgcolor:
                      "#f8fafc",
                  }}
                >
                  <Typography
                    variant="caption"

                    color="text.secondary"

                    fontWeight={700}
                  >
                    Valeur de référence calculée
                  </Typography>

                  <Typography
                    variant="body2"

                    fontWeight={900}

                    color="#0f172a"
                  >
                    {formatUsd(
                      computedPrices.baseUsd
                    )}
                  </Typography>

                  {computedPrices.baseCdf >
                  0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {formatCdf(
                        computedPrices.baseCdf
                      )}
                    </Typography>
                  ) : null}
                </Paper>
              </Box>
            </Paper>


            {/* =================================================
                COMMERCIAL PRICES
            ================================================= */}

            {formData.can_sell && (
              <Paper
                variant="outlined"

                sx={{
                  p: {
                    xs: 1.4,
                    sm: 1.7,
                  },

                  borderRadius: 3,

                  borderColor:
                    "rgba(148, 163, 184, 0.25)",
                }}
              >
                <Typography
                  fontWeight={900}
                  color="#0f172a"
                >
                  Prix commerciaux
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"

                  sx={{
                    display: "block",

                    mt: 0.25,

                    mb: 1.4,
                  }}
                >
                  Calcul automatique à partir du prix de base.
                </Typography>


                <Box
                  sx={{
                    display: "grid",

                    gridTemplateColumns:
                      {
                        xs: "1fr",

                        md:
                          "repeat(3, minmax(0, 1fr))",
                      },

                    gap: 1.4,
                  }}
                >

                  {/* DISTRIBUTEUR */}

                  <Paper
                    variant="outlined"

                    sx={{
                      p: 1.3,

                      borderRadius:
                        2.5,

                      bgcolor:
                        "#f8fafc",
                    }}
                  >
                    <Typography
                      variant="body2"

                      fontWeight={900}

                      sx={{
                        mb: 1,
                      }}
                    >
                      Distributeur principal
                    </Typography>

                    <TextField
                      size="small"

                      label="Marge (%)"

                      name="maison_djo_margin_percent"

                      type="number"

                      value={
                        formData.maison_djo_margin_percent
                      }

                      onChange={
                        handleChange
                      }

                      fullWidth

                      disabled={
                        saving
                      }
                    />

                    <Typography
                      variant="caption"

                      color="text.secondary"

                      sx={{
                        display:
                          "block",

                        mt: 0.8,
                      }}
                    >
                      Prix calculé
                    </Typography>

                    <Typography
                      fontWeight={900}

                      color="#0f172a"
                    >
                      {formatUsd(
                        computedPrices.distributorUsd
                      )}
                    </Typography>
                  </Paper>


                  {/* STRUCTURE SECONDAIRE */}

                  <Paper
                    variant="outlined"

                    sx={{
                      p: 1.3,

                      borderRadius:
                        2.5,

                      bgcolor:
                        "#f8fafc",
                    }}
                  >
                    <Typography
                      variant="body2"

                      fontWeight={900}

                      sx={{
                        mb: 1,
                      }}
                    >
                      Structure secondaire
                    </Typography>

                    <TextField
                      size="small"

                      label="Marge (%)"

                      name="depot_relais_margin_percent"

                      type="number"

                      value={
                        formData.depot_relais_margin_percent
                      }

                      onChange={
                        handleChange
                      }

                      fullWidth

                      disabled={
                        saving
                      }
                    />

                    <Typography
                      variant="caption"

                      color="text.secondary"

                      sx={{
                        display:
                          "block",

                        mt: 0.8,
                      }}
                    >
                      Prix calculé
                    </Typography>

                    <Typography
                      fontWeight={900}

                      color="#0f172a"
                    >
                      {formatUsd(
                        computedPrices.relayUsd
                      )}
                    </Typography>
                  </Paper>


                  {/* TERRAIN */}

                  <Paper
                    variant="outlined"

                    sx={{
                      p: 1.3,

                      borderRadius:
                        2.5,

                      bgcolor:
                        "#f8fafc",
                    }}
                  >
                    <Typography
                      variant="body2"

                      fontWeight={900}

                      sx={{
                        mb: 1,
                      }}
                    >
                      Terrain / Agent
                    </Typography>

                    <TextField
                      size="small"

                      label="Marge (%)"

                      name="terrain_margin_percent"

                      type="number"

                      value={
                        formData.terrain_margin_percent
                      }

                      onChange={
                        handleChange
                      }

                      fullWidth

                      disabled={
                        saving
                      }
                    />

                    <Typography
                      variant="caption"

                      color="text.secondary"

                      sx={{
                        display:
                          "block",

                        mt: 0.8,
                      }}
                    >
                      Prix calculé
                    </Typography>

                    <Typography
                      fontWeight={900}

                      color="#0f172a"
                    >
                      {formatUsd(
                        computedPrices.terrainUsd
                      )}
                    </Typography>
                  </Paper>
                </Box>
              </Paper>
            )}


            {/* =================================================
                STATUS + DESCRIPTION
            ================================================= */}

            <Box
              sx={{
                display: "grid",

                gridTemplateColumns:
                  {
                    xs: "1fr",

                    md:
                      "minmax(180px, 0.45fr) minmax(0, 1.55fr)",
                  },

                gap: 1.5,
              }}
            >
              <TextField
                select

                size="small"

                label="Statut"

                name="is_active"

                value={
                  String(
                    formData.is_active
                  )
                }

                onChange={(
                  event
                ) =>
                  setFormData(
                    (previous) => ({
                      ...previous,

                      is_active:
                        event.target.value ===
                        "true",
                    })
                  )
                }

                disabled={
                  saving
                }

                fullWidth
              >
                <MenuItem value="true">
                  Actif
                </MenuItem>

                <MenuItem value="false">
                  Inactif
                </MenuItem>
              </TextField>


              <TextField
                label="Description"

                name="description"

                value={
                  formData.description ||
                  ""
                }

                onChange={
                  handleChange
                }

                multiline

                minRows={3}

                disabled={
                  saving
                }

                fullWidth

                placeholder="Informations complémentaires sur l'article..."
              />
            </Box>
          </Stack>
        </DialogContent>


        {/* =================================================
            DIALOG ACTIONS
        ================================================= */}

        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            disabled={
              saving
            }

            onClick={
              handleClose
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"

            disableElevation

            disabled={
              saving
            }

            onClick={
              handleSave
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedProduct
              ? "Modifier"
              : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          4000
        }

        onClose={
          closeSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }

          variant="filled"

          onClose={
            closeSnackbar
          }

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default ProductsPage